import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { store } from '../store/store';
import { selectCurrentUser, selectSessionId, setSessionId } from '../store/slices/userSlice';
import {
   captureUtmSnapshot,
   getAnalyticsAppVersion,
   getOrCreateAnonSessionId,
   getOrCreateVisitorId,
} from './analyticsVisitor';
import { isAnalyticsDebug } from './analyticsDebug';

/**
 * ================================================================================================
 * STANDARDIZED METADATA USAGE GUIDE
 * ================================================================================================
 *
 * Use these helper functions to ensure consistent metadata across all trackUserAction calls:
 *
 * HELPER FUNCTIONS:
 * ================
 * 1. createAuthMetadata(action, email?, authMethod?, overrides?)     - For authentication actions
 * 2. createErrorMetadata(action, errorCode?, errorMessage?, overrides?) - For error tracking
 * 3. createPageMetadata(action, pagePath, pageTitle?, overrides?)    - For page navigation
 * 4. createTripMetadata(action, tripId?, tripTitle?, overrides?)     - For trip-related actions
 * 5. createPhotoMetadata(action, photoId?, photoTitle?, overrides?)  - For photo actions
 * 6. createEventMetadata(action, eventId?, eventTitle?, overrides?)  - For event actions
 * 7. createPerformanceMetadata(action, startTime, overrides?)        - For performance tracking
 * 8. createMetadata(action, overrides?)                              - Generic metadata
 *
 * TRACKING FUNCTIONS:
 * ==================
 * 1. trackUserAction(payload)                    - Main tracking function (requires authentication)
 * 2. trackPageView(path, title?, data?, userId?) - Authenticated page views
 * 3. trackAnonymousPageView(path, title?, data?) - Anonymous page views (no auth required)
 * 4. trackAnonymousAction(actionType, category, metadata?, description?) - Anonymous actions (no auth required)
 * 5. trackSmartPageView(path, title?, data?)     - Auto-detects auth status and tracks appropriately
 * 6. trackUserInteraction(type, targetId?, ...)  - User interactions
 * 7. trackContentAction(action, contentId, ...)  - Content actions
 * 8. trackError(type, message, stack?, ...)      - Error tracking
 *
 * STANDARD FIELDS AUTOMATICALLY SET:
 * ==================================
 * - action: Specific action identifier (e.g., 'login_attempt', 'signup_success')
 * - source: Component/form origin (e.g., 'auth_form', 'page_navigation')
 * - context: Situation context (e.g., 'authentication', 'navigation', 'error')
 * - targetType: Type of target entity (e.g., 'user', 'trip', 'photo')
 * - authMethod: Authentication method (e.g., 'email_password', 'google')
 * - userEmail: User's email for auth actions
 * - errorCode/errorMessage: Error details for error tracking
 * - pagePath/pageTitle: Page information for navigation
 *
 * USAGE EXAMPLES:
 * ===============
 * // Authentication
 * createAuthMetadata('login_success', user.email, 'google', { isNewUser: true })
 *
 * // Error tracking
 * createErrorMetadata('signup_failure', error.code, error.message, { source: 'signup_form' })
 *
 * // Page navigation (authenticated)
 * trackPageView('/dashboard', 'Dashboard')
 *
 * // Page navigation (anonymous - login/signup pages)
 * trackAnonymousPageView('/login', 'Login Page')
 *
 * // Anonymous actions (login attempts, signup attempts)
 * trackAnonymousAction(ACTION_TYPES.LOGIN, ACTION_CATEGORIES.USER, metadata, 'Login attempt')
 *
 * // Smart page navigation (auto-detects authentication)
 * trackSmartPageView('/profile', 'Profile Page')
 *
 * // Generic with custom fields
 * createMetadata('custom_action', { source: 'custom_component', customField: 'value' })
 * ================================================================================================
 */

if (isAnalyticsDebug()) {
   console.log('🚀 [ActionTracker] Module loaded', { db: !!db, store: !!store });
}

// Types for user action tracking
export interface BaseActionPayload {
   userId?: string; // Made optional since we can get it from Redux
   actionType: string;
   timestamp?: Timestamp;
   sessionId?: string;
   deviceInfo?: DeviceInfo;
   location?: LocationInfo;
}

export interface DeviceInfo {
   userAgent?: string;
   platform?: string;
   screenResolution?: string;
   browserInfo?: string;
   isMobile?: boolean;
}

export interface LocationInfo {
   latitude?: number;
   longitude?: number;
   city?: string;
   country?: string;
   timezone?: string;
}

// Standardized metadata structure for consistency
export interface StandardMetadata {
   // Core action info
   action?: string; // Specific action identifier (e.g., 'trip_creation_start', 'photo_upload_complete')

   // Context information
   context?: string; // Additional context about where/how the action occurred
   source?: string; // Source component/page/feature

   // Target information
   targetId?: string; // ID of the primary target (trip ID, photo ID, etc.)
   targetType?: string; // Type of target (trip, photo, event, user, etc.)
   targetTitle?: string; // Human-readable title of the target

   // Performance metrics
   duration?: number; // Duration in seconds for long-running operations
   startTime?: number; // Start timestamp for tracking operation duration
   endTime?: number; // End timestamp for tracking operation duration

   // User experience metrics
   stepCount?: number; // Number of steps in a multi-step process
   currentStep?: number; // Current step in a multi-step process
   errorCount?: number; // Number of errors encountered
   retryCount?: number; // Number of retries attempted

   // Content metrics
   contentLength?: number; // Length of text content (descriptions, etc.)
   fileSize?: number; // Size of uploaded files
   fileCount?: number; // Number of files involved
   fileTypes?: string[]; // Types of files involved

   // Relationship data
   associatedTripId?: string;
   associatedEventId?: string;
   associatedUserId?: string;
   parentId?: string; // Parent entity ID
   parentType?: string; // Parent entity type

   // State changes
   changes?: Record<string, any>; // What changed in an update operation
   previousValue?: any; // Previous value before change
   newValue?: any; // New value after change

   // Feature-specific data
   features?: string[]; // List of features used
   settings?: Record<string, any>; // Settings or configuration used

   // Additional custom data
   [key: string]: any; // Allow for additional custom metadata
}

export interface UserActionPayload extends BaseActionPayload {
   // Generic payload that can contain any additional data
   metadata?: StandardMetadata;
   category?: string;
   subcategory?: string;
   value?: number;
   description?: string;
   referrer?: string;
   targetId?: string; // ID of the item being acted upon (event, trip, photo, etc.)
   targetType?: string; // Type of the item (event, trip, photo, user, etc.)
}

// Helper functions for creating standardized metadata
export const createMetadata = (
   baseAction: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   const metadata = {
      action: baseAction,
      source: 'web_app',
      ...overrides,
   };

   // Filter out undefined values
   return cleanUndefinedValues(metadata);
};

// Trip-specific metadata helpers
export const createTripMetadata = (
   action: string,
   tripId?: string,
   tripTitle?: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   return createMetadata(action, {
      source: 'trip_management',
      context: 'trip',
      targetId: tripId,
      targetType: 'trip',
      targetTitle: tripTitle,
      ...overrides,
   });
};

// Photo-specific metadata helpers
export const createPhotoMetadata = (
   action: string,
   photoId?: string,
   photoTitle?: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   return createMetadata(action, {
      source: 'photo_management',
      context: 'photo',
      targetId: photoId,
      targetType: 'photo',
      targetTitle: photoTitle,
      ...overrides,
   });
};

// Event-specific metadata helpers
export const createEventMetadata = (
   action: string,
   eventId?: string,
   eventTitle?: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   return createMetadata(action, {
      source: 'event_management',
      context: 'event',
      targetId: eventId,
      targetType: 'event',
      targetTitle: eventTitle,
      ...overrides,
   });
};

// Performance tracking helpers
export const createPerformanceMetadata = (
   action: string,
   startTime: number,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   const endTime = Date.now();
   const duration = (endTime - startTime) / 1000;

   return createMetadata(action, {
      source: 'performance_monitor',
      context: 'performance',
      startTime,
      endTime,
      duration,
      ...overrides,
   });
};

// Authentication-specific metadata helpers
export const createAuthMetadata = (
   action: string,
   email?: string,
   authMethod?: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   const metadata = createMetadata(action, {
      source: 'auth_form',
      context: 'authentication',
      targetType: 'user',
      authMethod: authMethod || 'email_password',
      userEmail: email,
      ...overrides,
   });

   // Filter out undefined values
   return cleanUndefinedValues(metadata);
};

// Error tracking metadata helpers
export const createErrorMetadata = (
   action: string,
   errorCode?: string,
   errorMessage?: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   const metadata = createMetadata(action, {
      source: 'error_handler',
      context: 'error',
      errorCode,
      errorMessage,
      ...overrides,
   });

   // Filter out undefined values
   return cleanUndefinedValues(metadata);
};

// Page view metadata helpers
export const createPageMetadata = (
   action: string,
   pagePath: string,
   pageTitle?: string,
   overrides: Partial<StandardMetadata> = {}
): StandardMetadata => {
   const metadata = createMetadata(action, {
      source: 'page_navigation',
      context: 'navigation',
      pagePath,
      pageTitle,
      referrer: document.referrer || 'direct',
      ...overrides,
   });

   // Filter out undefined values
   return cleanUndefinedValues(metadata);
};

// Predefined action types for consistency
export const ACTION_TYPES = {
   // Navigation actions
   PAGE_VIEW: 'page_view',
   PAGE_EXIT: 'page_exit',

   // User interactions
   CLICK: 'click',
   SCROLL: 'scroll',
   SEARCH: 'search',
   FILTER: 'filter',
   SORT: 'sort',

   // Content actions
   VIEW_CONTENT: 'view_content',
   SHARE_CONTENT: 'share_content',
   LIKE_CONTENT: 'like_content',
   COMMENT: 'comment',
   DOWNLOAD: 'download',

   // Trip related actions
   CREATE_TRIP: 'create_trip',
   EDIT_TRIP: 'edit_trip',
   DELETE_TRIP: 'delete_trip',
   JOIN_TRIP: 'join_trip',
   LEAVE_TRIP: 'leave_trip',
   VIEW_TRIP: 'view_trip',

   // Event related actions
   CREATE_EVENT: 'create_event',
   EDIT_EVENT: 'edit_event',
   DELETE_EVENT: 'delete_event',
   JOIN_EVENT: 'join_event',
   LEAVE_EVENT: 'leave_event',
   VIEW_EVENT: 'view_event',

   // Photo related actions
   UPLOAD_PHOTO: 'upload_photo',
   DELETE_PHOTO: 'delete_photo',
   VIEW_PHOTO: 'view_photo',
   TRANSFORM_PHOTO: 'transform_photo',

   // User management actions
   SIGNUP: 'signup',
   LOGIN: 'login',
   LOGOUT: 'logout',
   PROFILE_UPDATE: 'profile_update',

   // Payment actions
   PURCHASE: 'purchase',
   SUBSCRIPTION: 'subscription',
   PAYMENT_METHOD_ADD: 'payment_method_add',

   // Social actions
   SEND_INVITATION: 'send_invitation',
   ACCEPT_INVITATION: 'accept_invitation',
   CONNECT_USER: 'connect_user',

   /** Photo sharing modal (collections) */
   PHOTO_SHARE_MODAL_OPENED: 'photo_share_modal_opened',
   PHOTO_SHARE_LINK_CREATED: 'photo_share_link_created',
   PHOTO_SHARE_LINK_COPIED: 'photo_share_link_copied',
   PHOTO_SHARE_EMAIL_SENT: 'photo_share_email_sent',

   // Error tracking
   ERROR: 'error',
   API_ERROR: 'api_error',

   // Custom action
   CUSTOM: 'custom',
} as const;

export const ACTION_CATEGORIES = {
   NAVIGATION: 'navigation',
   USER_INTERACTION: 'user_interaction',
   CONTENT: 'content',
   TRIP: 'trip',
   EVENT: 'event',
   PHOTO: 'photo',
   USER: 'user',
   PAYMENT: 'payment',
   SOCIAL: 'social',
   ERROR: 'error',
   CUSTOM: 'custom',
} as const;

// Collection name for user actions
const USER_ACTIONS_COLLECTION = 'userActions';

/** Common fields for joining client events with server-side public analytics. */
function analyticsEnvelope(): Record<string, string | Record<string, string>> {
   return {
      visitorId: getOrCreateVisitorId(),
      anonSessionId: getOrCreateAnonSessionId(),
      utm: captureUtmSnapshot(),
      appVersion: getAnalyticsAppVersion(),
   };
}

/**
 * Validates that all required fields are present in the action payload
 * @param payload - The action payload to validate
 * @returns Object with validation results
 */
const validateRequiredFields = (
   payload: UserActionPayload
): {
   isValid: boolean;
   missingFields: string[];
   warnings: string[];
} => {
   const missingFields: string[] = [];
   const warnings: string[] = [];

   // Check required fields
   if (!payload.actionType) {
      missingFields.push('actionType');
   }

   // Check for recommended fields and provide defaults if missing
   if (!payload.category) {
      warnings.push('category not provided, will use default: CUSTOM');
   }

   if (!payload.description) {
      warnings.push('description not provided, will generate default');
   }

   if (!payload.metadata?.source) {
      warnings.push('metadata.source not provided, will use default: web_app');
   }

   return {
      isValid: missingFields.length === 0,
      missingFields,
      warnings,
   };
};

/**
 * Helper function to get current user ID from Redux store
 * @returns string | null - Returns user ID or null if not authenticated
 */
const getCurrentUserId = (): string | null => {
   try {
      const state = store.getState();
      const currentUser = selectCurrentUser(state);
      const userId = currentUser?.uid || null;
      if (isAnalyticsDebug()) {
         console.log('🔍 [ActionTracker] userId:', userId);
      }
      return userId;
   } catch (error) {
      if (isAnalyticsDebug()) {
         console.warn('⚠️ [ActionTracker] Failed to get current user ID from Redux:', error);
      }
      return null;
   }
};

/**
 * Helper function to get or generate session ID from Redux store
 * @returns string - Returns session ID, generates new one if needed
 */
const getSessionId = (): string => {
   try {
      const state = store.getState();
      let sessionId = selectSessionId(state);

      if (!sessionId) {
         sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
         store.dispatch(setSessionId(sessionId));
      }

      return sessionId;
   } catch (error) {
      if (isAnalyticsDebug()) {
         console.warn('⚠️ [ActionTracker] Session ID fallback:', error);
      }
      return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
   }
};

/**
 * Main function to track user actions
 * @param payload - The action payload containing all relevant information
 * @returns Promise<string> - Returns the document ID of the tracked action
 */
export const trackUserAction = async (payload: UserActionPayload): Promise<string> => {
   try {
      // Get userId from payload or Redux store
      const userId = payload.userId || getCurrentUserId();

      // Validate required fields
      if (!userId) {
         if (isAnalyticsDebug()) {
            console.warn('⚠️ [ActionTracker] No user ID — skipping trackUserAction', {
               actionType: payload.actionType,
            });
         }
         throw new Error('User ID is required. User must be logged in to track actions.');
      }

      // Validate required fields
      const validation = validateRequiredFields(payload);

      if (!validation.isValid) {
         console.error('❌ [ActionTracker] Missing required fields:', validation.missingFields);
         throw new Error(`Missing required fields: ${validation.missingFields.join(', ')}`);
      }

      if (isAnalyticsDebug() && validation.warnings.length > 0) {
         console.warn('⚠️ [ActionTracker] Field warnings:', validation.warnings);
      }

      // Get session ID from Redux
      const sessionId = payload.sessionId || getSessionId();

      // Ensure all required fields are present with proper defaults
      const category = payload.category || ACTION_CATEGORIES.CUSTOM;
      const description = payload.description || `Action: ${payload.actionType}`;
      const source = payload.metadata?.source || 'web_app';
      const env = analyticsEnvelope();

      // Enhance payload with additional system information and required fields
      const enhancedPayload = {
         ...payload,
         userId, // Ensure userId is always present
         actionType: payload.actionType, // Explicitly set actionType
         category, // Ensure category is always present
         description, // Ensure description is always present
         timestamp: Timestamp.now(), // Use Timestamp.now() for immediate resolution
         sessionId, // Use Redux session ID
         deviceInfo: payload.deviceInfo || getDeviceInfo(),
         visitorId: env.visitorId as string,
         anonSessionId: env.anonSessionId as string,
         // Enhance metadata with source if not already present
         metadata: {
            source,
            ...payload.metadata,
            visitorId: env.visitorId,
            anonSessionId: env.anonSessionId,
            utm: env.utm,
            appVersion: env.appVersion,
         },
         // Add environment info
         environment: process.env.NODE_ENV || 'development',
         version: process.env.REACT_APP_VERSION || '1.0.0',
      };

      // Clean undefined values to prevent Firestore errors
      const cleanedPayload = cleanUndefinedValues(enhancedPayload);

      if (isAnalyticsDebug()) {
         console.log('🔍 [ActionTracker] trackUserAction', payload.actionType, cleanedPayload);
      }

      // Save to Firestore
      const docRef = await addDoc(collection(db, USER_ACTIONS_COLLECTION), cleanedPayload);

      return docRef.id;
   } catch (error) {
      if (isAnalyticsDebug()) {
         console.error('❌ [ActionTracker] Error tracking user action:', error);
      }
      throw error;
   }
};

/**
 * Convenience function for tracking page views
 */
export const trackPageView = async (
   pagePath: string,
   pageTitle?: string,
   additionalData?: Record<string, any>,
   userId?: string // Optional override
): Promise<string | null> => {
   try {
      // Get userId from parameter or Redux store
      const resolvedUserId = userId || getCurrentUserId();

      // If no user is logged in, log the attempt but don't throw an error
      if (!resolvedUserId) {
         if (isAnalyticsDebug()) {
            console.log('📊 [ActionTracker] Page view skipped (no user)', pagePath);
         }
         return null;
      }

      return trackUserAction({
         userId: resolvedUserId,
         actionType: ACTION_TYPES.PAGE_VIEW,
         category: ACTION_CATEGORIES.NAVIGATION,
         description: `Page view: ${pagePath}`,
         metadata: {
            pagePath,
            pageTitle,
            referrer: document.referrer,
            ...additionalData,
            // visitorId / anonSessionId / utm also applied in trackUserAction
         },
      });
   } catch (error) {
      console.warn('⚠️ [ActionTracker] Failed to track page view:', error);
      return null;
   }
};

/**
 * Track anonymous page views for unauthenticated users
 * Stores minimal data without requiring user authentication
 */
export const trackAnonymousPageView = async (
   pagePath: string,
   pageTitle?: string,
   additionalData?: Record<string, any>
): Promise<string | null> => {
   try {
      const anonymousSessionId = getOrCreateAnonSessionId();
      const visitorId = getOrCreateVisitorId();
      const env = analyticsEnvelope();

      const anonymousPayload = {
         actionType: ACTION_TYPES.PAGE_VIEW,
         category: ACTION_CATEGORIES.NAVIGATION,
         description: `Anonymous page view: ${pagePath}`,
         sessionId: anonymousSessionId,
         visitorId,
         timestamp: Timestamp.now(),
         deviceInfo: getDeviceInfo(),
         environment: process.env.NODE_ENV || 'development',
         version: process.env.REACT_APP_VERSION || '1.0.0',
         isAnonymous: true,
         metadata: {
            pagePath,
            pageTitle,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            ...env,
            ...additionalData,
         },
      };

      if (isAnalyticsDebug()) {
         console.log('📊 [ActionTracker] anonymous page view', pagePath);
      }

      // Clean undefined values to prevent Firestore errors
      const cleanedPayload = cleanUndefinedValues(anonymousPayload);

      // Save to a separate collection for anonymous tracking
      const docRef = await addDoc(collection(db, 'anonymousActions'), cleanedPayload);

      return docRef.id;
   } catch (error) {
      if (isAnalyticsDebug()) {
         console.warn('⚠️ [ActionTracker] Failed to track anonymous page view:', error);
      }
      return null;
   }
};

/**
 * Track anonymous actions for unauthenticated users (e.g., login attempts, signup attempts)
 * Stores action data without requiring user authentication
 */
export const trackAnonymousAction = async (
   actionType: string,
   category: string,
   metadata?: Record<string, any>,
   description?: string
): Promise<string | null> => {
   try {
      const anonymousSessionId = getOrCreateAnonSessionId();
      const visitorId = getOrCreateVisitorId();
      const env = analyticsEnvelope();

      const anonymousPayload = {
         actionType,
         category,
         description: description || `Anonymous action: ${actionType}`,
         sessionId: anonymousSessionId,
         visitorId,
         timestamp: Timestamp.now(),
         deviceInfo: getDeviceInfo(),
         environment: process.env.NODE_ENV || 'development',
         version: process.env.REACT_APP_VERSION || '1.0.0',
         isAnonymous: true,
         metadata: {
            userAgent: navigator.userAgent,
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            ...env,
            ...metadata,
         },
      };

      if (isAnalyticsDebug()) {
         console.log('📊 [ActionTracker] anonymous action', actionType);
      }

      // Clean undefined values to prevent Firestore errors
      const cleanedPayload = cleanUndefinedValues(anonymousPayload);

      // Save to a separate collection for anonymous tracking
      const docRef = await addDoc(collection(db, 'anonymousActions'), cleanedPayload);

      return docRef.id;
   } catch (error) {
      if (isAnalyticsDebug()) {
         console.warn('⚠️ [ActionTracker] Failed to track anonymous action:', error);
      }
      return null;
   }
};

/**
 * Smart page view tracking that automatically chooses between authenticated and anonymous tracking
 */
export const trackSmartPageView = async (
   pagePath: string,
   pageTitle?: string,
   additionalData?: Record<string, any>
): Promise<string | null> => {
   try {
      const userId = getCurrentUserId();
      const merged = { ...analyticsEnvelope(), ...additionalData };

      if (userId) {
         return trackPageView(pagePath, pageTitle, merged, userId);
      }
      return trackAnonymousPageView(pagePath, pageTitle, merged);
   } catch (error) {
      if (isAnalyticsDebug()) {
         console.warn('⚠️ [ActionTracker] Failed to track smart page view:', error);
      }
      return null;
   }
};

/** Client-side errors for signed-out users (never throws). */
export const trackAnonymousClientError = async (
   message: string,
   extra?: Record<string, any>
): Promise<string | null> => {
   return trackAnonymousAction(
      ACTION_TYPES.ERROR,
      ACTION_CATEGORIES.ERROR,
      {
         action: 'client_error',
         errorMessage: message,
         url: typeof window !== 'undefined' ? window.location.href : undefined,
         ...extra,
      },
      `Anonymous client error: ${message}`,
   );
};

/**
 * Convenience function for tracking user interactions
 */
export const trackUserInteraction = async (
   interactionType: string,
   targetId?: string,
   targetType?: string,
   additionalData?: Record<string, any>,
   userId?: string // Optional override
): Promise<string> => {
   return trackUserAction({
      userId,
      actionType: interactionType,
      category: ACTION_CATEGORIES.USER_INTERACTION,
      description: `User interaction: ${interactionType}${targetType ? ` on ${targetType}` : ''}`,
      targetId,
      targetType,
      metadata: {
         source: 'user_interface',
         context: 'interaction',
         ...additionalData,
      },
   });
};

/**
 * Convenience function for tracking content actions
 */
export const trackContentAction = async (
   actionType: string,
   contentId: string,
   contentType: string,
   additionalData?: Record<string, any>,
   userId?: string // Optional override
): Promise<string> => {
   return trackUserAction({
      userId,
      actionType,
      category: ACTION_CATEGORIES.CONTENT,
      description: `Content action: ${actionType} on ${contentType}`,
      targetId: contentId,
      targetType: contentType,
      metadata: {
         source: 'content_manager',
         context: 'content',
         ...additionalData,
      },
   });
};

/**
 * Convenience function for tracking errors
 */
export const trackError = async (
   errorType: string,
   errorMessage: string,
   errorStack?: string,
   additionalData?: Record<string, any>,
   userId?: string // Optional override
): Promise<string> => {
   return trackUserAction({
      userId,
      actionType: ACTION_TYPES.ERROR,
      category: ACTION_CATEGORIES.ERROR,
      description: `Error: ${errorType} - ${errorMessage}`,
      metadata: {
         source: 'error_handler',
         context: 'error',
         errorType,
         errorMessage,
         errorStack,
         url: window.location.href,
         ...additionalData,
      },
   });
};

/**
 * Batch tracking function for multiple actions
 */
export const trackMultipleActions = async (actions: UserActionPayload[]): Promise<string[]> => {
   const promises = actions.map((action) => trackUserAction(action));
   return Promise.all(promises);
};

// Helper functions
function getDeviceInfo(): DeviceInfo {
   // Safely access screen dimensions
   const getScreenResolution = (): string => {
      try {
         if (typeof window !== 'undefined' && window.screen) {
            return `${window.screen.width}x${window.screen.height}`;
         }
         return 'unknown';
      } catch {
         return 'unknown';
      }
   };

   return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: getScreenResolution(),
      browserInfo: getBrowserInfo(),
      isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
         navigator.userAgent
      ),
   };
}

/**
 * Clean undefined values from an object recursively
 * Firestore doesn't allow undefined values, so we need to remove them
 */
function cleanUndefinedValues(obj: any): any {
   if (obj === null || obj === undefined) {
      return null;
   }

   if (Array.isArray(obj)) {
      return obj.map(cleanUndefinedValues).filter((item) => item !== undefined);
   }

   if (typeof obj === 'object') {
      const cleaned: any = {};
      Object.keys(obj).forEach((key) => {
         const value = cleanUndefinedValues(obj[key]);
         if (value !== undefined) {
            cleaned[key] = value;
         }
      });
      return cleaned;
   }

   return obj;
}

function getBrowserInfo(): string {
   const ua = navigator.userAgent;
   let browserName = 'Unknown';

   if (ua.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
   } else if (ua.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
   } else if (ua.indexOf('Safari') > -1) {
      browserName = 'Safari';
   } else if (ua.indexOf('Edge') > -1) {
      browserName = 'Edge';
   } else if (ua.indexOf('Opera') > -1) {
      browserName = 'Opera';
   }

   return browserName;
}

/**
 * Get user's location (requires permission)
 */
export const getCurrentLocation = (): Promise<LocationInfo> => {
   return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
         reject(new Error('Geolocation is not supported by this browser'));
         return;
      }

      navigator.geolocation.getCurrentPosition(
         (position) => {
            resolve({
               latitude: position.coords.latitude,
               longitude: position.coords.longitude,
               timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
         },
         (error) => {
            console.warn('Location access denied:', error);
            resolve({
               timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
         }
      );
   });
};

/**
 * Enhanced tracking with location
 */
export const trackUserActionWithLocation = async (payload: UserActionPayload): Promise<string> => {
   try {
      const location = await getCurrentLocation();
      return trackUserAction({
         ...payload,
         location,
      });
   } catch (error) {
      // Fallback to tracking without location
      return trackUserAction(payload);
   }
};

/**
 * Quick tracking functions with simplified parameters
 * These automatically get user ID from Redux
 */

// Quick click tracking
export const trackClick = async (
   elementId: string,
   elementType?: string,
   metadata?: Record<string, any>
) => {
   return trackUserInteraction(ACTION_TYPES.CLICK, elementId, elementType || 'element', {
      source: 'user_interface',
      context: 'click_interaction',
      ...metadata,
   });
};

// Quick page view tracking
export const trackPage = async (path?: string, title?: string, metadata?: Record<string, any>) => {
   const pagePath = path || window.location.pathname;
   const pageTitle = title || document.title;
   return trackPageView(pagePath, pageTitle, {
      source: 'page_navigation',
      context: 'navigation',
      ...metadata,
   });
};

// Quick search tracking
export const trackSearch = async (
   query: string,
   resultsCount?: number,
   metadata?: Record<string, any>
) => {
   return trackUserAction({
      actionType: ACTION_TYPES.SEARCH,
      category: ACTION_CATEGORIES.USER_INTERACTION,
      description: `Search query: "${query}"${resultsCount !== undefined ? ` (${resultsCount} results)` : ''}`,
      metadata: {
         source: 'search_interface',
         context: 'search',
         searchQuery: query,
         resultsCount,
         ...metadata,
      },
   });
};

// Quick error tracking
export const trackQuickError = async (
   error: Error | string,
   context?: string,
   metadata?: Record<string, any>
) => {
   const errorMessage = typeof error === 'string' ? error : error.message;
   const errorStack = typeof error === 'string' ? undefined : error.stack;

   return trackError('javascript_error', errorMessage, errorStack, {
      source: 'error_handler',
      context: context || 'application_error',
      ...metadata,
   });
};

/**
 * Debug function to test the action tracker
 * Call this from browser console: window.testActionTracker()
 */
export const testActionTracker = async () => {
   console.log('🧪 [ActionTracker] Starting debug test...');

   try {
      // Test basic functionality
      const testPayload = {
         actionType: 'test_action',
         category: ACTION_CATEGORIES.CUSTOM,
         description: 'Testing action tracker functionality',
         metadata: {
            source: 'debug_console',
            context: 'testing',
            test: true,
            timestamp: new Date().toISOString(),
         },
      };

      console.log('🧪 [ActionTracker] Test payload:', testPayload);
      const result = await trackUserAction(testPayload);
      console.log('✅ [ActionTracker] Test successful! Document ID:', result);
      return result;
   } catch (error) {
      console.error('❌ [ActionTracker] Test failed:', error);
      throw error;
   }
};

/**
 * Diagnostic function to check system state
 * Call this from browser console: window.diagnoseActionTracker()
 */
export const diagnoseActionTracker = () => {
   console.log('🔬 [ActionTracker] Running diagnostics...');

   try {
      // Check Redux store
      console.log('🔬 [ActionTracker] Checking Redux store...');
      const state = store.getState();
      console.log('🔬 [ActionTracker] Full Redux state:', state);

      // Check user state
      const currentUser = selectCurrentUser(state);
      console.log('🔬 [ActionTracker] Current user:', currentUser);
      console.log('🔬 [ActionTracker] User ID:', currentUser?.uid);
      console.log('🔬 [ActionTracker] User authenticated:', !!currentUser?.uid);

      // Check session state
      const sessionId = selectSessionId(state);
      console.log('🔬 [ActionTracker] Session ID:', sessionId);

      // Check Firebase config
      console.log('🔬 [ActionTracker] Firebase db instance:', db);
      console.log('🔬 [ActionTracker] Collection name:', USER_ACTIONS_COLLECTION);

      // Check environment
      console.log('🔬 [ActionTracker] Environment:', process.env.NODE_ENV);
      console.log('🔬 [ActionTracker] App version:', process.env.REACT_APP_VERSION);

      return {
         userAuthenticated: !!currentUser?.uid,
         userId: currentUser?.uid,
         sessionId,
         environment: process.env.NODE_ENV,
         firebaseConfigured: !!db,
      };
   } catch (error) {
      console.error('❌ [ActionTracker] Diagnostic failed:', error);
      return {
         error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
   }
};

/**
 * Test Firebase connectivity and permissions
 * Call this from browser console: window.testFirebaseConnection()
 */
export const testFirebaseConnection = async () => {
   console.log('🔥 [ActionTracker] Testing Firebase connection...');

   try {
      // Test if we can access the collection
      const testCollection = collection(db, USER_ACTIONS_COLLECTION);
      console.log('🔥 [ActionTracker] Collection reference created:', testCollection);

      // Try to create a simple test document
      const testDoc = {
         test: true,
         timestamp: Timestamp.now(),
         message: 'Firebase connection test',
      };

      console.log('🔥 [ActionTracker] Attempting to write test document...');
      const docRef = await addDoc(testCollection, testDoc);
      console.log('✅ [ActionTracker] Firebase write successful! Doc ID:', docRef.id);

      return {
         success: true,
         docId: docRef.id,
         collection: USER_ACTIONS_COLLECTION,
      };
   } catch (error) {
      console.error('❌ [ActionTracker] Firebase connection test failed:', error);
      return {
         success: false,
         error: error instanceof Error ? error.message : 'Unknown Firebase error',
         errorCode: error instanceof Error && 'code' in error ? (error as any).code : 'unknown',
      };
   }
};

// Make test function available globally in development
if (process.env.NODE_ENV === 'development') {
   (window as any).testActionTracker = testActionTracker;
   (window as any).diagnoseActionTracker = diagnoseActionTracker;
   (window as any).testFirebaseConnection = testFirebaseConnection;
   (window as any).trackUserAction = trackUserAction;
   (window as any).testCleanUndefinedValues = () => {
      const testObject = {
         defined: 'value',
         undefinedValue: undefined,
         nullValue: null,
         emptyString: '',
         zeroValue: 0,
         falseValue: false,
         nested: {
            definedNested: 'nested value',
            undefinedNested: undefined,
            nullNested: null,
         },
         array: [1, undefined, 'value', null, 0, false],
         deepNested: {
            level1: {
               level2: {
                  defined: 'deep value',
                  undefined: undefined,
                  null: null,
               },
            },
         },
         // Test tracking metadata scenarios
         associatedTripId: undefined,
         associatedEventId: '',
         someId: null,
      };

      console.log('🧪 Original object:', testObject);
      const cleaned = cleanUndefinedValues(testObject);
      console.log('🧪 Cleaned object:', cleaned);

      // Verify no undefined values exist
      const hasUndefined = JSON.stringify(cleaned).includes('undefined');
      console.log('🧪 Contains undefined values:', hasUndefined);

      return { original: testObject, cleaned, hasUndefined };
   };
   console.log(
      '🧪 [ActionTracker] Debug functions available: window.testActionTracker(), window.diagnoseActionTracker(), window.testFirebaseConnection(), window.trackUserAction(), window.testCleanUndefinedValues()'
   );

   (window as any).debugActionTracker = () => {
      console.log('🔧 [ActionTracker] Basic debug info:', {
         db: !!db,
         store: !!store,
         user: selectCurrentUser(store.getState())?.uid,
      });
      return 'Debug info logged above';
   };

   (window as any).testActionTrackerSimple = async () => {
      console.log('🧪 [ActionTracker] Running simple test (no auth required)...');
      try {
         const testPayload = {
            userId: 'test-user-123',
            actionType: 'test_simple',
            category: ACTION_CATEGORIES.CUSTOM,
            description: 'Simple test without authentication',
            metadata: {
               source: 'debug_console',
               context: 'testing',
               test: true,
               timestamp: new Date().toISOString(),
            },
         };
         const result = await trackUserAction(testPayload);
         console.log('✅ [ActionTracker] Simple test successful! Document ID:', result);
         return result;
      } catch (error) {
         console.error('❌ [ActionTracker] Simple test failed:', error);
         return { error: error instanceof Error ? error.message : 'Unknown error' };
      }
   };
}
